import { useState } from "react";
import { CreditCard, Pencil, Plus, Users, HeartPulse, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { EmptyState, PageHeader, RowsSkeleton, Tag } from "@/components/app/kit";
import { FormDialog } from "@/components/app/FormDialog";
import { ModuleSelector } from "@/components/platform/ModuleSelector";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const num = (v) => (v === "" || v == null ? null : Number(v));
const toPayload = (v) => ({
  name: v.name, description: v.description || "", price_monthly: num(v.price_monthly), currency: v.currency, trial_days: Number(v.trial_days || 0),
  active: !!v.active, modules: v.modules || [], limits: { max_users: Number(v.max_users), max_patients: num(v.max_patients), storage_mb: Number(v.storage_mb) },
});

export default function Plans() {
  const { me } = useAuth();
  const isSuper = me.user.platform_role === "super_admin";
  const { data, loading, reload } = useFetch("/platform/plans");
  const mods = useFetch("/platform/modules");
  const [edit, setEdit] = useState(null);
  const fields = [
    ...(edit?.isNew ? [{ key: "code", label: "Code", required: true, mono: true }] : []),
    { key: "name", label: "Nom", required: true },
    { key: "price_monthly", label: "Prix mensuel", type: "number", hint: "Vide = à définir" },
    { key: "currency", label: "Devise", required: true },
    { key: "trial_days", label: "Jours d'essai", type: "number", required: true },
    { key: "max_users", label: "Utilisateurs max.", type: "number", required: true },
    { key: "max_patients", label: "Patients max.", type: "number", hint: "Vide = illimité" },
    { key: "storage_mb", label: "Stockage (Mo)", type: "number", required: true },
    { key: "active", label: "Formule active", type: "switch" },
    { key: "description", label: "Description", type: "textarea", rows: 2 },
  ];
  const open = (p) => setEdit(p ? { ...p, ...p.limits, max_patients: p.limits.max_patients ?? "", price_monthly: p.price_monthly ?? "" } : { isNew: true, currency: "EUR", trial_days: 30, active: true, max_users: 5, storage_mb: 1024, modules: [] });
  return (
    <div>
      <PageHeader eyebrow="Offre commerciale" title="Plans d'abonnement" subtitle="Chaque formule définit les modules inclus et les limites par défaut appliqués à la création d'un cabinet."
        actions={isSuper && <Button onClick={() => open(null)} data-testid="plans-create-button"><Plus className="h-4 w-4 mr-2" />Nouvelle formule</Button>} />
      {loading ? <RowsSkeleton /> : !data?.length ? <EmptyState icon={CreditCard} title="Aucune formule" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {data.map((p) => (
            <div key={p.code} className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-6 flex flex-col animate-fade-up" data-testid={`plan-card-${p.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-lg font-bold font-heading">{p.name}</p>
                  <p className="font-mono text-xs text-slate-400">{p.code}</p>
                </div>
                <div className="flex gap-2">{!p.active && <Tag tone="gray">Inactive</Tag>}<Tag tone="sky">{p.tenants_count} cabinet(s)</Tag></div>
              </div>
              <p className="text-sm text-slate-500 mt-2">{p.description}</p>
              <p className="text-2xl font-extrabold font-heading mt-4">{p.price_monthly != null ? `${p.price_monthly} ${p.currency}` : <span className="text-base text-slate-400 font-semibold">Tarif à définir</span>}<span className="text-sm text-slate-400 font-normal"> / mois</span></p>
              <div className="grid grid-cols-3 gap-2 mt-4 text-xs text-slate-600">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{p.limits.max_users}</span>
                <span className="flex items-center gap-1"><HeartPulse className="h-3.5 w-3.5" />{p.limits.max_patients ?? "∞"}</span>
                <span className="flex items-center gap-1"><HardDrive className="h-3.5 w-3.5" />{p.limits.storage_mb} Mo</span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4 flex-1 content-start">{p.modules.map((m) => <Tag key={m} tone="gray">{m}</Tag>)}</div>
              {isSuper && <Button variant="outline" className="mt-5" onClick={() => open(p)} data-testid={`plan-edit-${p.code}`}><Pencil className="h-4 w-4 mr-2" />Modifier</Button>}
            </div>
          ))}
        </div>
      )}
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} wide testid="plan-dialog" title={edit?.isNew ? "Nouvelle formule" : `Modifier « ${edit?.name} »`}
        initial={edit || {}} fields={fields}
        extra={(v, setV) => mods.data && (
          <div className="space-y-2"><p className="text-xs font-semibold text-slate-700">Modules inclus</p>
            <ModuleSelector modules={mods.data} value={v.modules || []} onChange={(m) => setV((s) => ({ ...s, modules: m }))} planModules={v.modules || []} />
          </div>
        )}
        onSubmit={async (v) => {
          if (edit.isNew) await api.post("/platform/plans", { code: v.code, ...toPayload(v) });
          else await api.put(`/platform/plans/${edit.code}`, toPayload(v));
          toast.success("Formule enregistrée");
          reload();
        }} />
    </div>
  );
}
